/**
 * Searchable single or multi-select menu used for every issue property.
 * Number keys pick the first nine options; typing filters.
 */
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { useState, type ReactNode } from "react";

export interface PickerOption<V> {
  value: V;
  label: string;
  icon?: ReactNode;
  hint?: string;
  group?: string;
  keywords?: string[];
}

interface Base<V> {
  options: PickerOption<V>[];
  placeholder?: string;
  children: ReactNode;
  align?: "start" | "center" | "end";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  footer?: ReactNode;
  emptyText?: string;
  triggerClassName?: string;
  disabled?: boolean;
}

type Single<V> = Base<V> & { multiple?: false; value: V; onChange: (v: V) => void };
type Multi<V> = Base<V> & { multiple: true; value: V[]; onChange: (v: V[]) => void };

export function Picker<V extends string | number | null>(props: Single<V> | Multi<V>) {
  const [innerOpen, setInnerOpen] = useState(false);
  const open = props.open ?? innerOpen;
  const setOpen = props.onOpenChange ?? setInnerOpen;
  const selected = (v: V) => (props.multiple ? props.value.includes(v) : props.value === v);

  const choose = (v: V) => {
    if (props.multiple) {
      const next = props.value.includes(v) ? props.value.filter((x) => x !== v) : [...props.value, v];
      props.onChange(next);
    } else {
      props.onChange(v);
      setOpen(false);
    }
  };

  const groups = new Map<string, PickerOption<V>[]>();
  for (const o of props.options) {
    const g = o.group ?? "";
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(o);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={props.disabled} className={props.triggerClassName}>
        {props.children}
      </PopoverTrigger>
      <PopoverContent align={props.align ?? "start"} className="w-64 p-0" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
        <Command
          onKeyDown={(e) => {
            const n = Number(e.key);
            if (!props.multiple && n >= 1 && n <= 9 && !e.metaKey && !e.ctrlKey && (e.target as HTMLInputElement).value === "") {
              const o = props.options[n - 1];
              if (o) {
                e.preventDefault();
                choose(o.value);
              }
            }
          }}
        >
          <CommandInput placeholder={props.placeholder ?? "Search…"} className="h-9" />
          <CommandList className="max-h-72">
            <CommandEmpty>{props.emptyText ?? "Nothing matches."}</CommandEmpty>
            {[...groups.entries()].map(([g, opts]) => (
              <CommandGroup key={g || "_"} heading={g || undefined}>
                {opts.map((o, i) => (
                  <CommandItem key={String(o.value)} value={`${o.label} ${String(o.value)} ${(o.keywords ?? []).join(" ")}`} onSelect={() => choose(o.value)} className="gap-2">
                    {props.multiple && (
                      <span className={cn("flex size-4 items-center justify-center rounded border", selected(o.value) ? "border-brand bg-brand text-brand-foreground" : "border-input")}>
                        {selected(o.value) && <Check className="size-3" />}
                      </span>
                    )}
                    {o.icon}
                    <span className="flex-1 truncate">{o.label}</span>
                    {o.hint && <span className="text-xs text-muted-foreground">{o.hint}</span>}
                    {!props.multiple && selected(o.value) && <Check className="size-3.5 text-muted-foreground" />}
                    {!props.multiple && !g && i < 9 && !selected(o.value) && props.options.length <= 12 && <span className="w-3 text-right text-[11px] text-muted-foreground/70">{i + 1}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
          {props.footer}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
